import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import TableComponent from "../../components/TableComponent";
import { ENUM_ROUTES } from "../../RoutesEnum";
import { useLazyGetBannerDetailQuery } from "../../services/banner";
import { showError } from "../../constants/toasts";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

const ManageBannerDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [valueTabs, setValueTabs] = React.useState(0);
  const [bannerDetails] = useLazyGetBannerDetailQuery();
  const [bannerData, setBannerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await bannerDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        setBannerData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const venueRows =
    bannerData?.venueStorageId?.map((venue: any, index: number) => ({
      _id: venue?._id,
      name: venue.name,
      type: venue.type,
      status: venue.status,
      guestAllowed: venue.guestAllowed,
      address: venue.address || "N/A",
      createdAt: formatDate(venue.createdAt),
      description: venue.description,
      pricing: venue.setPricing,
      images: venue.images,
      facilityIncluded: venue.facilityIncluded,
      isRecommended: venue.isRecommended,
      visibilityTogle: venue.visibilityTogle,
      latitude: venue.latitude,
      longitude: venue.longitude,
      securityAmount: venue.securityAmount,
      securityFee: venue.securityFee,
      cleaningFee: venue.cleaningFee,
      insuranceFee: venue.insuranceFee,
      cateringFee: venue.cateringFee,
      decorationFee: venue.decorationFee,
    })) || [];

  const venueColumns = [
    {
      field: "name",
      headerName: "Venue Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <div style={{ fontWeight: "bold" }}>{params.value}</div>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => (
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "4px",
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            fontSize: "12px",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case "APPROVED":
              return { bg: "#e8f5e8", color: "#2e7d32" };
            case "PENDING":
              return { bg: "#fff3e0", color: "#f57c00" };
            case "REJECTED":
              return { bg: "#ffebee", color: "#d32f2f" };
            default:
              return { bg: "#f5f5f5", color: "#757575" };
          }
        };
        const colors = getStatusColor(params.value);
        return (
          <span
            style={{
              padding: "4px 8px",
              borderRadius: "4px",
              backgroundColor: colors.bg,
              color: colors.color,
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      field: "guestAllowed",
      headerName: "Guest Capacity",
      flex: 1,
      minWidth: 130,
      renderCell: (params: any) => (
        <span style={{ fontWeight: "500" }}>{params.value} guests</span>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => (
        <div
          style={{
            maxWidth: "180px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "12px",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "pricing",
      headerName: "Pricing",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <div style={{ fontSize: "12px" }}>
          <div>Hour: ₹{params.value?.perHour}</div>
          <div>Day: ₹{params.value?.perDay}</div>
          <div>Person: ₹{params.value?.perPerson}</div>
        </div>
      ),
    },
    {
      field: "facilityIncluded",
      headerName: "Facilities",
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => (
        <div style={{ fontSize: "12px" }}>
          {params.value && params.value.length > 0 ? (
            params.value.map((facility: string, index: number) => (
              <span
                key={index}
                style={{
                  display: "inline-block",
                  margin: "1px",
                  padding: "2px 6px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "3px",
                  fontSize: "10px",
                }}
              >
                {facility.replace("_", " ")}
              </span>
            ))
          ) : (
            <span style={{ color: "#999", fontSize: "10px" }}>
              No facilities
            </span>
          )}
        </div>
      ),
    },
    {
      field: "isRecommended",
      headerName: "Recommended",
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => (
        <span
          style={{
            padding: "2px 6px",
            borderRadius: "3px",
            backgroundColor: params.value ? "#e8f5e8" : "#ffebee",
            color: params.value ? "#2e7d32" : "#d32f2f",
            fontSize: "10px",
          }}
        >
          {params.value ? "Yes" : "No"}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 1,
      minWidth: 130,
      renderCell: (params: any) => (
        <span style={{ fontSize: "12px", color: "#666" }}>{params.value}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <MainContainer>
        <div className="main_title">
          <h1>Banner Details</h1>
          <p>Loading...</p>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Banner Details</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate(ENUM_ROUTES.MANAGE_BANNERS)}>
            Manage Banners
          </span>{" "}
          - Banner Details
        </p>
      </div>

      <Box className="custom_tabs1" sx={{ width: "100%" }}>
        <Tabs
          value={valueTabs}
          onChange={handleChangeTabs}
          className="custom_tabs_links"
        >
          <Tab label="General Details" {...a11yProps(0)} />
          <Tab label="Linked Venues" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <div style={{ marginTop: 20 }}></div>
      <CustomTabPanel value={valueTabs} index={0}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Ad Banner Information</h2>
          </div>
          <div className="gap_p">
            <div className="vew_img w_100">
              <h6>Banner Image</h6>
              <figure>
                <img
                  alt="banner"
                  src={bannerData?.image || "/static/images/placeholder.png"}
                  style={{ maxWidth: "300px", height: "auto" }}
                />
              </figure>
            </div>
            <div className="w_33">
              <h6>Ad Title</h6>
              <p>{bannerData?.title || "N/A"}</p>
            </div>
            <div className="w_33">
              <h6>Linked Venues Count</h6>
              <p>{bannerData?.venueStorageId?.length || 0} venues</p>
            </div>
            <div className="w_33">
              <h6>Add Frequency</h6>
              <p>{bannerData?.addFrequency || "N/A"}</p>
            </div>
            <div className="w_33">
              <h6>Duration</h6>
              <p>{bannerData?.duration || "N/A"} days</p>
            </div>
            <div className="w_33">
              <h6>Start Date</h6>
              <p>
                {bannerData?.startDate
                  ? formatDate(bannerData.startDate)
                  : "N/A"}
              </p>
            </div>
            <div className="w_33">
              <h6>End Date</h6>
              <p>
                {bannerData?.endDate ? formatDate(bannerData.endDate) : "N/A"}
              </p>
            </div>
            <div className="w_33">
              <h6>Status</h6>
              <p>{bannerData?.isActive ? "Active" : "Inactive"}</p>
            </div>
            <div className="w_33">
              <h6>Created Date</h6>
              <p>
                {bannerData?.createdAt
                  ? formatDate(bannerData.createdAt)
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={valueTabs} index={1}>
        <div className="cards">
          <TableComponent columns={venueColumns} rows={venueRows} />
        </div>
      </CustomTabPanel>
    </MainContainer>
  );
};

export default ManageBannerDetails;
